import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  Chip, 
  CircularProgress,  
  Table, 
  TableBody,
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Tooltip, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { 
  GetUsersBIZidentity 
} from '../../../../redux/actions/business/identity/usersActions';

const UsersPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [usersList, setUsersList] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  // a verified/unverified tick against an empty field reads as "unverified"
  // when the field simply isn't set — show the marker only when there's a value
  const contactLine = (value, verified, label) => (
    value
      ?
      <Tooltip title={label}>
        <Box className="flexCenter_Row" gap={"10px"}>
          {
            verified === true
            ? <CheckCircleOutlineOutlinedIcon color='success'/>
            : <CancelOutlinedIcon color='error'/>
          }
          <Typography>{value}</Typography>
        </Box>
      </Tooltip>
      :
      null
  );

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Username",
      sort: "user_name",
    },
    {
      label: "Name",
      sort: "first_name",
    },
    {
      label: "Is login allowed",
      sort: "is_login_allowed",
    }, 
    {
      label: "Emails",
      sort: "primary_email",
    },
    {
      label: "Mobiles",
      sort: "primary_mobile",
    },
    {
      label: "Status",
      sort: "account_status",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    }
  ];

  useEffect(() => {
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetUsersBIZidentity(page+1, rowsPerPage, query, setLoading));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatUsersList(state.usersList.records || []);
      setUsersTotal(state.usersList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatUsersList = (list) => {
    setUsersList(list);
  };

  return (
    <Box>

      <TopBar
        title={"Users"}
        search={search}
        searchPlaceholder={"Search id, username, name, email or mobile"}
        onSearchChange={handleSearchChange}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          usersList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No users found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                usersList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.user_name || <NA />}</TableCell>
                    <TableCell>
                      <Typography>
                        {row.first_name || <NA />}&nbsp;{row.last_name || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      { 
                        row.is_login_allowed === true
                        ? <CheckCircleOutlineOutlinedIcon color='success'/>
                        : <CancelOutlinedIcon color='error'/>
                      } 
                    </TableCell>
                    <TableCell>
                      {contactLine(row.primary_email, row.is_primary_email_verified, "Primary Email")}
                      {contactLine(row.secondary_email, row.is_secondary_email_verified, "Secondary Email")}
                      {!row.primary_email && !row.secondary_email && <NA />}
                    </TableCell>
                    <TableCell>
                      {contactLine(
                        row.primary_mobile && `${row.primary_mobile_country_code || ""} ${row.primary_mobile}`.trim(),
                        row.is_primary_mobile_verified,
                        "Primary Mobile"
                      )}
                      {contactLine(
                        row.secondary_mobile && `${row.secondary_mobile_country_code || ""} ${row.secondary_mobile}`.trim(),
                        row.is_secondary_mobile_verified,
                        "Secondary Mobile"
                      )}
                      {!row.primary_mobile && !row.secondary_mobile && <NA />}
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={row.account_status === "active" 
                          ? "success" 
                          : row.account_status === "pending"
                            ? "warning"
                            : "error" }
                        size='small'
                        label={row.account_status || "unknown"} 
                        />
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={usersTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default UsersPage;